import React from 'react';
import { sortBy } from 'lodash';

import {
  getAccessoryCategoryType,
  getNormalizedProductType
} from '../product/product';
import { isArrayWithLength } from '../validators/validators';

import { AXLE_TYPES } from '../../constants/fitment';
import { PRODUCT_IMAGE_FORMATS } from '../../constants/product-images';
import { IMAGE_TYPES, PRODUCT_TYPES } from '../../constants/products';
import { SUGGESTED_SELLING_CONTEXTS } from '../../constants/suggested-selling';

export function getCompletedCanonicalFilterSelections(
  filterKeyValPairs,
  incompleteSelectedFilters
) {
  return filterKeyValPairs.reduce((acc, keyVal) => {
    if (
      keyVal.value &&
      keyVal.value !== incompleteSelectedFilters[keyVal.key]?.value
    ) {
      acc[keyVal.key] = {
        label: keyVal.value,
        value: keyVal.value,
        wasAutoSelected: true
      };
    }

    return acc;
  }, {});
}

export function getFilterOptions(selectedFilters) {
  const filterOptions = [];

  if (selectedFilters && typeof selectedFilters === 'object') {
    for (let key in selectedFilters) {
      if (selectedFilters[key]) {
        filterOptions.push({ name: key, value: selectedFilters[key]?.value });
      }
    }
  }

  return filterOptions;
}

export function getGalleryImages(galleryData, isPackage) {
  let images = galleryData?.product?.gallery;

  if (isPackage && galleryData) {
    images = (
      galleryData?.product?.gallery1?.map(i => ({
        ...i,
        productType: PRODUCT_TYPES.TIRES
      })) || []
    ).concat(
      galleryData?.product?.gallery2?.map(i => ({
        ...i,
        productType: PRODUCT_TYPES.WHEELS
      })) || []
    );
  }

  const gallery = {};

  if (isArrayWithLength(images)) {
    const galleryImages = images.filter(i => {
      const matchingImage = format => image =>
        image.galleryIndex === i.galleryIndex &&
        image.format === format &&
        image.productType === i.productType;

      if (process.env.__FF_PRODUCT_IMAGE_FORMATS_AUTO_SYNC__ === 'true') {
        return (
          i?.imageType === IMAGE_TYPES.GALLERY &&
          images.find(matchingImage(PRODUCT_IMAGE_FORMATS.PRODUCT_LARGE)) &&
          images.find(matchingImage(PRODUCT_IMAGE_FORMATS.PRODUCT_MEDIUM)) &&
          images.find(matchingImage(PRODUCT_IMAGE_FORMATS.PRODUCT_X_SMALL))
        );
      } else {
        return (
          i?.imageType === IMAGE_TYPES.GALLERY &&
          images.find(matchingImage(PRODUCT_IMAGE_FORMATS.ZOOM)) &&
          images.find(matchingImage(PRODUCT_IMAGE_FORMATS.PRODUCT)) &&
          images.find(matchingImage(PRODUCT_IMAGE_FORMATS.THUMBNAIL))
        );
      }
    });

    if (isArrayWithLength(galleryImages)) {
      for (let i = 0; i < galleryImages.length; i++) {
        const image = galleryImages[i];
        const { format } = image || {};

        if (gallery[format]) {
          gallery[format].push(image);
        } else {
          gallery[format] = [image];
        }
      }
    }
  }

  for (let key in gallery) {
    gallery[key] = sortBy(gallery[key], 'galleryIndex');
  }

  return gallery;
}

export function getSuggestedSellingAxleType(prevPageUrl, isStaggered) {
  let axleType = AXLE_TYPES.BOTH;

  if (prevPageUrl && isStaggered) {
    if (prevPageUrl.includes('front')) {
      axleType = AXLE_TYPES.FRONT;
    } else if (prevPageUrl.includes('rear')) {
      axleType = AXLE_TYPES.REAR;
    }
  }

  return axleType;
}

export function getSuggestedSellingPageContext(
  pageType,
  productName = '',
  isTreadwellFlow
) {
  let pageContext;

  if (pageType === PRODUCT_TYPES.TIRES) {
    pageContext = SUGGESTED_SELLING_CONTEXTS.PDP_TIRE;
    if (isTreadwellFlow) {
      pageContext = SUGGESTED_SELLING_CONTEXTS.PDP_TIRE_PDL;
    }
  } else if (pageType === PRODUCT_TYPES.WHEELS) {
    pageContext = SUGGESTED_SELLING_CONTEXTS.PDP_WHEEL;
  } else if (pageType === PRODUCT_TYPES.ACCESSORIES) {
    const accessoryCategoryType = getAccessoryCategoryType(productName);

    pageContext =
      SUGGESTED_SELLING_CONTEXTS[`PDP_${accessoryCategoryType}_ACCESSORY`];
  }

  return pageContext;
}

export function getProductLD({ imageUrl, prebuiltPackage, product }) {
  if (!product && !prebuiltPackage) return null;

  let productLD;

  if (prebuiltPackage) {
    const { product1, product2 } = prebuiltPackage;
    let jsonLD = {
      '@context': 'http://schema.org',
      '@type': 'ItemList',
      itemListElement: [],
      numberOfItems: '2',
      url: window.location.href
    };

    [product1, product2].map((product, idx) => {
      const {
        brand,
        description,
        longDescription,
        name,
        price,
        reviewSummaryData
      } = product || {};

      jsonLD['itemListElement'].push({
        '@type': 'Product',
        brand: {
          '@type': 'Brand',
          name: brand
        },
        description:
          longDescription?.replace(/(<([^>]+)>)/gi, '') || description,
        image: imageUrl,
        name: name,
        offers: {
          '@type': 'Offer',
          availability: 'http://schema.org/InStoreOnly',
          price: price?.value,
          priceCurrency: 'USD'
        },
        position: idx + 1,
        url: window.location.href
      });

      if (reviewSummaryData) {
        jsonLD.aggregateRating = {
          '@type': 'AggregateRating',
          ratingValue: reviewSummaryData.totalRating,
          reviewCount: reviewSummaryData.totalCount
        };
      }
    });
    productLD = jsonLD;
  } else {
    const {
      aggregates,
      brand,
      description,
      longDescription,
      name,
      price,
      priceRange,
      productType,
      reviewSummaryData
    } = product || {};

    productLD = {
      '@context': 'http://schema.org',
      '@type': 'Product',
      brand: {
        '@type': 'Brand',
        name: brand
      },
      description: longDescription?.replace(/(<([^>]+)>)/gi, '') || description,
      image: imageUrl,
      name: name,
      url: window.location.href
    };

    if (reviewSummaryData) {
      productLD.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: reviewSummaryData.totalRating,
        reviewCount: reviewSummaryData.totalCount
      };
    }

    if (productType === 'aggregate' && priceRange) {
      productLD.offers = {
        '@type': 'AggregateOffer',
        availability: 'http://schema.org/InStoreOnly',
        highPrice: priceRange.maxPrice?.value,
        lowPrice: priceRange.minPrice?.value,
        offerCount: aggregates?.codes?.length,
        offers: {
          '@type': 'Offer',
          url: window.location.href
        },
        priceCurrency: 'USD'
      };
    } else {
      productLD.offers = {
        '@type': 'Offer',
        availability: 'http://schema.org/InStoreOnly',
        price: price?.value,
        priceCurrency: 'USD'
      };
    }
  }

  return JSON.stringify(productLD);
}

// Not a typical component because react-helmet doesn't understand React components
export function getOpenGraphProductTags(
  metaData,
  product,
  prebuiltPackage,
  imageUrl
) {
  let metaProduct;

  if (prebuiltPackage) {
    metaProduct =
      getNormalizedProductType(prebuiltPackage?.product1?.productType) ===
      PRODUCT_TYPES.TIRES
        ? prebuiltPackage.product1
        : prebuiltPackage?.product2;
  } else {
    metaProduct = product;
  }

  if (!metaProduct) return null;

  const { brand, name } = metaProduct;
  const { content } = metaData?.find(m => m.name === 'description') || {};

  const nameWithUrl = `${name} at ${
    window.isDT ? 'www.Discounttire.com' : 'www.Americastire.com'
  }. `;

  const re = new RegExp(name);
  const description = content
    ? content.replace(re, nameWithUrl)
    : `Find ${brand} ${name} for your vehicle right here at ${window.siteName}.`;

  return [
    <meta content={description} key="mt-1" name="og:description" />,
    content && <meta content={imageUrl} key="mt-2" name="og:image" />,
    <meta content={description} key="mt-3" name="og:title" />
  ];
}

export const getGalleryImagesByProductType = (galleryImages, productType) =>
  Object.keys(galleryImages).reduce((acc, currVal) => {
    acc[currVal] = galleryImages[currVal].filter(
      item => item.productType === productType
    );
    return acc;
  }, {});
